// Sidebar — пункты меню по доступным tools пользователя.
//
// Контракт: бэкенд возвращает список tools в /api/auth/me/.
// Для is_superuser дополнительно показываем секцию «АДМИН» (PRD §3.2).
// Иконки выбираются по category, fallback — общий «инструмент».
import { Layout, Menu } from "antd";
import type { MenuProps } from "antd";
import {
  PictureOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  UserOutlined,
  HistoryOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import type { MeResponse, ToolDescriptor } from "@/api/types";

const CATEGORY_LABELS: Record<string, string> = {
  graphic: "ГРАФИКА",
  text: "ТЕКСТ",
};

function iconForTool(tool: ToolDescriptor) {
  if (tool.category === "graphic") return <PictureOutlined />;
  if (tool.category === "text") return <FileTextOutlined />;
  return <AppstoreOutlined />;
}

function groupTools(tools: ToolDescriptor[]): Record<string, ToolDescriptor[]> {
  const groups: Record<string, ToolDescriptor[]> = {};
  for (const t of tools) {
    if (t.category === "admin") continue; // admin-инструменты показываем отдельно
    const cat = t.category || "graphic";
    (groups[cat] ||= []).push(t);
  }
  return groups;
}

interface Props {
  me: MeResponse;
}

export function AppSidebar({ me }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Слаг tool'а из URL — для подсветки активного пункта.
  // Поддерживает как /tools/banner_generator, так и /tools/banner_generator/history.
  // Бэкенд использует подчёркивания в slug'ах (banner_generator), фронт — то же.
  const groups = groupTools(me.tools);

  const items: MenuProps["items"] = Object.entries(groups).map(([category, tools]) => ({
    type: "group" as const,
    key: `group:${category}`,
    label: CATEGORY_LABELS[category] ?? category.toUpperCase(),
    children: tools.map((t) => ({
      key: `/tools/${t.slug}`,
      icon: iconForTool(t),
      label: t.name,
    })),
  }));

  if (me.is_superuser) {
    items.push({
      type: "group" as const,
      key: "group:admin",
      label: "АДМИН",
      children: [
        // Per-user permissions страница — /admin/users/:id/permissions, отдельный
        // Sidebar-пункт «Права доступа» удалён как избыточный: права назначаются
        // через кнопку «Доступы» в таблице пользователей (PRD §3.10).
        { key: "/admin/users", icon: <UserOutlined />, label: "Пользователи" },
        { key: "/admin/logs", icon: <HistoryOutlined />, label: "Логи генераций" },
        { key: "/admin/complaints", icon: <FlagOutlined />, label: "Жалобы" },
      ],
    });
  }

  // selectedKeys: матчим pathname к ключам меню. Подсветка работает на:
  //   /tools/{slug}/history  → /tools/{slug}     (Sidebar показывает tool)
  //   /admin/users/123/permissions → /admin/users (per-user → подсвечен Пользователи)
  // Остальное — exact match.
  const adminUsersMatch = /^\/admin\/users\b/;
  const toolHistoryMatch = /\/history\/?$/;
  let selected = pathname;
  if (toolHistoryMatch.test(selected)) {
    selected = selected.replace(toolHistoryMatch, "");
  } else if (adminUsersMatch.test(selected)) {
    selected = "/admin/users";
  }

  return (
    <Layout.Sider
      width={240}
      breakpoint="lg"
      collapsible
      collapsedWidth={64}
      style={{ background: "transparent" }}
    >
      <Menu
        mode="inline"
        selectedKeys={[selected]}
        items={items}
        onClick={({ key }) => {
          if (typeof key === "string" && key.startsWith("/")) navigate(key);
        }}
        style={{ height: "100%", borderRight: 0 }}
      />
    </Layout.Sider>
  );
}
